<template>
  <canvas ref="canvas" class="confetti-rain" aria-hidden="true"></canvas>
</template>

<script>
// Confetti "rain" — paper-style particles spawn across the full width of
// the top edge and flutter down through the viewport with a swaying
// horizontal drift and tumbling rotation, mimicking real paper confetti
// falling from a ceiling drop. Self-contained (no external library).
//
// Usage:
//   <ConfettiRain ref="confetti" />
//   this.$refs.confetti.fire()
//   this.$refs.confetti.stop()
//
// Honours `prefers-reduced-motion` — fire() is a no-op when the user has
// the OS setting on, so the rest of the celebration UI (banner, copy)
// still renders normally.

const DEFAULT_COLORS = [
  '#16a07a', '#0e5b46', '#25d366',
  '#f59e0b', '#fbbf24', '#fb923c', '#f97316',
  '#ef4444', '#3b82f6',
  '#a855f7', '#ec4899'
]

export default {
  name: 'ConfettiRain',
  props: {
    // How long the top edge keeps spawning particles (ms). After this,
    // already-falling pieces finish their descent — total visible time
    // is duration + ~1.5s tail.
    duration: { type: Number, default: 2000 },
    // Particles spawned per frame across the top edge. With the default
    // 8 at 60fps, that's ~480 pieces per second of fire.
    particleCountPerFrame: { type: Number, default: 8 },
    // Initial downward speed (px/frame). Variance is layered on top so
    // pieces fall at different rates and look natural.
    fallSpeed: { type: Number, default: 3 },
    colors: { type: Array, default: () => DEFAULT_COLORS }
  },
  data () {
    return {
      handle: null,
      particles: [],
      endsAt: 0,
      width: 0,
      height: 0
    }
  },
  beforeDestroy () {
    this.stop()
  },
  methods: {
    fire () {
      const canvas = this.$refs.canvas
      if (!canvas || !canvas.getContext) return
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

      this.stop()
      this.resizeCanvas()
      this.endsAt = Date.now() + this.duration
      this.loop()
    },
    stop () {
      if (this.handle) {
        window.cancelAnimationFrame(this.handle)
        this.handle = null
      }
      this.particles = []
      this.endsAt = 0
      const canvas = this.$refs.canvas
      if (canvas && canvas.getContext) {
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
    },
    resizeCanvas () {
      const canvas = this.$refs.canvas
      const dpr = window.devicePixelRatio || 1
      this.width = window.innerWidth
      this.height = window.innerHeight
      canvas.width = this.width * dpr
      canvas.height = this.height * dpr
      canvas.style.width = `${this.width}px`
      canvas.style.height = `${this.height}px`
      const ctx = canvas.getContext('2d')
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(dpr, dpr)
    },
    spawnRow () {
      for (let i = 0; i < this.particleCountPerFrame; i++) {
        // Random x along the full top edge, y just above the viewport.
        const x = Math.random() * this.width
        const y = -20 - Math.random() * 40
        this.particles.push({
          x,
          y,
          vx: (Math.random() - 0.5) * 1.5,
          vy: this.fallSpeed * (0.6 + Math.random() * 0.9),
          size: 5 + Math.random() * 7,
          rot: Math.random() * Math.PI * 2,
          vr: (Math.random() - 0.5) * 0.25,
          color: this.colors[Math.floor(Math.random() * this.colors.length)],
          shape: Math.random() < 0.5 ? 'rect' : 'circle',
          // Sway parameters — each piece has its own sine-wave phase so
          // the field doesn't look like a synchronised march.
          driftPhase: Math.random() * Math.PI * 2,
          driftSpeed: 0.03 + Math.random() * 0.05,
          driftMag: 0.8 + Math.random() * 1.8
        })
      }
    },
    loop () {
      const canvas = this.$refs.canvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      const gravity = 0.04

      const tick = () => {
        ctx.clearRect(0, 0, this.width, this.height)

        if (Date.now() < this.endsAt) this.spawnRow()

        let alive = 0
        for (let i = 0; i < this.particles.length; i++) {
          const p = this.particles[i]
          if (p.y > this.height + 40) continue
          p.driftPhase += p.driftSpeed
          p.vy += gravity
          p.x += p.vx + Math.sin(p.driftPhase) * p.driftMag
          p.y += p.vy
          p.rot += p.vr
          alive += 1
          ctx.save()
          ctx.translate(p.x, p.y)
          ctx.rotate(p.rot)
          ctx.fillStyle = p.color
          if (p.shape === 'rect') {
            ctx.fillRect(-p.size / 2, -p.size / 3, p.size, p.size * 0.6)
          } else {
            ctx.beginPath()
            ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2)
            ctx.fill()
          }
          ctx.restore()
        }

        if (alive > 0 || Date.now() < this.endsAt) {
          this.handle = window.requestAnimationFrame(tick)
        } else {
          this.handle = null
          this.particles = []
          ctx.clearRect(0, 0, this.width, this.height)
        }
      }
      this.handle = window.requestAnimationFrame(tick)
    }
  }
}
</script>

<style scoped>
.confetti-rain {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1300;
}
</style>
